import { useQueries, useQuery, type Query, type QueryClient } from '@tanstack/react-query'
import { useMemberSession } from '@/entities/member'
import { getTask, listTasks, type ListTasksParams } from '../api/task-api'
import type { GenerationTask } from './generation-task'
import { isTerminalTaskStatus } from './task-display'

export const TASK_DETAIL_POLL_INTERVAL_MS = 3000
export const TASK_LIST_POLL_INTERVAL_MS = 5000
export const GLOBAL_TASK_LIST_PARAMS = { page: 1, pageSize: 20 } as const

export const taskKeys = {
  all: ['generation-tasks'] as const,
  quote: (signature: string) => [...taskKeys.all, 'quote', signature] as const,
  list: (params: ListTasksParams) => [...taskKeys.all, 'list', params] as const,
  detail: (taskNo: string) => [...taskKeys.all, 'detail', taskNo] as const,
}

interface GenerationTaskPage {
  page: number
  size: number
  total: number
  items: GenerationTask[]
}

/** Makes an accepted task immediately visible in the global queue. */
export function cacheCreatedTask(queryClient: QueryClient, task: GenerationTask) {
  queryClient.setQueryData(taskKeys.detail(task.taskNo), task)
  queryClient.setQueryData<GenerationTaskPage>(
    taskKeys.list(GLOBAL_TASK_LIST_PARAMS),
    (current) => {
      if (!current) return current
      const exists = current.items.some((item) => item.taskNo === task.taskNo)
      if (exists) {
        return {
          ...current,
          items: current.items.map((item) => item.taskNo === task.taskNo ? task : item),
        }
      }
      return {
        ...current,
        total: current.total + 1,
        items: [task, ...current.items].slice(0, current.size),
      }
    },
  )
}

export function useTasks(
  page = 1,
  size = 20,
  filters: Omit<ListTasksParams, 'page' | 'pageSize'> = {},
  options: { poll?: boolean } = {},
) {
  const authenticated = useMemberSession((state) => state.authenticated)
  const params = { page, pageSize: size, ...filters }
  return useQuery({
    queryKey: taskKeys.list(params),
    queryFn: async () => (await listTasks(params)).data,
    enabled: authenticated,
    refetchInterval: (query) => options.poll !== false && query.state.data?.items.some((task) => !isTerminalTaskStatus(task.status))
      ? TASK_LIST_POLL_INTERVAL_MS
      : false,
  })
}

export function useTask(taskNo?: string) {
  return useQuery({
    queryKey: taskKeys.detail(taskNo ?? ''),
    queryFn: async () => (await getTask(taskNo!)).data,
    enabled: Boolean(taskNo),
    refetchInterval: (query) => query.state.data && !isTerminalTaskStatus(query.state.data.status)
      ? TASK_DETAIL_POLL_INTERVAL_MS
      : false,
  })
}

export function useTaskDetails(tasks: GenerationTask[]) {
  const authenticated = useMemberSession((state) => state.authenticated)
  const activeTasks = tasks.filter((task) => !isTerminalTaskStatus(task.status))
  const detailQueries = useQueries({
    queries: activeTasks.map((task) => ({
      queryKey: taskKeys.detail(task.taskNo),
      queryFn: async () => (await getTask(task.taskNo)).data,
      enabled: authenticated,
      refetchInterval: (query: Query<GenerationTask>) => query.state.data && isTerminalTaskStatus(query.state.data.status)
        ? false
        : TASK_DETAIL_POLL_INTERVAL_MS,
    })),
  })
  const detailsByTaskNo = new Map(detailQueries.flatMap((query) => query.data ? [[query.data.taskNo, query.data] as const] : []))
  return tasks.map((task) => detailsByTaskNo.get(task.taskNo) ?? task)
}
